
import Vue, { PropType } from 'vue';
import { RunnitNodeField } from '@run-diffusion/shared';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import _isEmpty from 'lodash/isEmpty';
import { RunnitDynamicFieldMixin } from '@/views/Runnits/RunnitSettings/mixins/RunnitDynamicFieldMixin';
import { getLogicOnFieldsFromField } from '@run-diffusion/shared';
import _isNil from 'lodash/isNil';
import { mapActions } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';

export default Vue.extend({
	name: 'SelectField',
	mixins: [
		RunnitDynamicFieldMixin,
	],
	props: {
		field: {
			type: Object as PropType<RunnitNodeField>,
			required: true,
		},
		value: {
			type: [String, Number, Object],
			required: false,
		},
		inputValues: { type: Object, required: true },
	},
	data () {
		return {
			loadedImages: {} as Record<string, boolean>,
			focusedIndex: 0,
			isOpen: false,
			typeBuffer: '',
			typeBufferTimeout: null as number | null,

			IMAGE_MIN_WIDTH: 115,
		};
	},
	computed: {
		logicOnFields () {
			return getLogicOnFieldsFromField(this.field, this.inputValues);
		},
		autoSetValue () {
			return this.logicOnFields.autoSetValue;
		},
		autoSetError () {
			return this.logicOnFields.autoSetError;
		},
		localValue () {
			return this.options.find((option) => option.value === this.value) || null;
		},
		useImageSelect () {
			return !_isEmpty(this.field?.display?.image);
		},
		useRadioSelect () {
			return !_isEmpty(this.field?.display?.radio);
		},
		useTextSelect () {
			return !_isEmpty(this.field?.display?.text);
		},
		imageMinWidth () {
			return this.field?.display?.image?.imageMinWidth || this.IMAGE_MIN_WIDTH;
		},
		options () {
			if (this.useImageSelect) return this.field?.display?.image?.options;
			if (this.useRadioSelect) return this.field?.display?.radio?.options;
			if (this.useTextSelect) return this.field?.display?.text?.options;
			return this.field?.display?.options || [];
		},
	},
	mounted () {
		window.addEventListener('keydown', this.handleKeyDown);
	},
	beforeDestroy () {
		window.removeEventListener('keydown', this.handleKeyDown);
		this.clearTypeBuffer();
	},
	watch: {
		autoSetValue: {
			handler (newValue: any, oldValue: any) {
				// If autoSet value changes and matches an available option, select it
				if (!_isNil(newValue) && newValue !== oldValue) {
					const option = (this.options || []).find((opt: any) => opt && opt.value === newValue);
					if (option) {
						const closer = (this.$refs.menuOrBottomSheet && (this.$refs.menuOrBottomSheet as any).close) || (() => { });
						this.onSelectOption(option, closer);
					} else {
						// Prefer server-provided message if any when autoSet value cannot be applied
						const msg = this.autoSetError || 'Selected value is not available in options. Please choose manually.';
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: msg,
							show: true,
						});
					}
				}
			},
			immediate: true,
		},
		autoSetError: {
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: newVal,
						show: true,
					});
				}
			},
			immediate: true,
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		onSelectOption (option: any, close: Function) {
			this.$emit('input', option.value);
			close();
		},
		onImageLoad (value: string) {
			this.$set(this.loadedImages, value, true);
		},
		onMenuOpen (isOpen: boolean, close: Function) {
			if (isOpen) {
				this.focusedIndex = 0;
				this.$nextTick(() => {
					const videos = document.querySelectorAll('video.runnit-images-select-image') as any;
					videos.forEach(video => {
						video.play().catch(() => { console.error('Error playing video'); });
					});
				});
			} else {
				const videos = document.querySelectorAll('video.runnit-images-select-image') as any;
				videos.forEach(video => {
					video.pause();
				});
			}
			this.isOpen = isOpen;
			if (close) {
				close();
			}
		},
		popTypeBuffer () {
			this.typeBuffer = this.typeBuffer.slice(0, -1);
			this.resetTypeBufferTimeout();
		},
		clearTypeBuffer () {
			this.typeBuffer = '';
			if (this.typeBufferTimeout) {
				clearTimeout(this.typeBufferTimeout);
				this.typeBufferTimeout = null;
			}
		},
		resetTypeBufferTimeout () {
			if (this.typeBufferTimeout) {
				clearTimeout(this.typeBufferTimeout);
			}
			this.typeBufferTimeout = setTimeout(() => {
				this.clearTypeBuffer();
			}, 1000);
		},
		handleTypeToFocus (key: string) {
			// Add the key to the buffer
			this.typeBuffer += key.toLowerCase();

			this.resetTypeBufferTimeout();

			// Find the first option that starts with the typed string
			const matchingIndex = this.options.findIndex(option =>
				option.text.toLowerCase().includes(this.typeBuffer)
			);

			if (matchingIndex !== -1) {
				this.focusedIndex = matchingIndex;
				this.$nextTick(() => {
					const container = document.querySelector('.v-menu__content');
					const focusedElement = container?.querySelector('.focused');
					if (focusedElement) {
						focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
					}
				});
			}
		},
		handleKeyDown (event: KeyboardEvent) {
			if (!this.isOpen) return;

			const focusedOption = this.options[this.focusedIndex];
			let shouldScroll = false;

			switch (event.key) {
				case 'ArrowUp':
					event.preventDefault();
					if (this.focusedIndex > 0) {
						this.focusedIndex--;
						shouldScroll = true;
					} else {
						this.focusedIndex = this.options.length - 1;
						shouldScroll = true;
					}
					this.clearTypeBuffer();
					break;
				case 'ArrowDown':
					event.preventDefault();
					if (this.focusedIndex < this.options.length - 1) {
						this.focusedIndex++;
						shouldScroll = true;
					} else {
						this.focusedIndex = 0;
						shouldScroll = true;
					}
					this.clearTypeBuffer();
					break;
				case 'Enter':
					event.preventDefault();
					if (focusedOption && this.$refs.menuOrBottomSheet) {
						this.onSelectOption(focusedOption, this.$refs.menuOrBottomSheet.close);
					}
					this.clearTypeBuffer();
					break;
				case 'Escape':
					event.preventDefault();
					if (this.$refs.menuOrBottomSheet) {
						this.$refs.menuOrBottomSheet.close();
					}
					this.clearTypeBuffer();
					break;
				case 'Backspace':
					event.preventDefault();
					this.popTypeBuffer();
					break;
				default:
					event.preventDefault();
					this.handleTypeToFocus(event.key);
					break;
			}

			if (shouldScroll) {
				this.$nextTick(() => {
					const container = document.querySelector('.v-menu__content');
					const focusedElement = container?.querySelector('.focused');
					if (focusedElement) {
						focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
					}
				});
			}
		},
		getHighlightedParts (text: string) {
			if (!this.typeBuffer) return [{ text, isMatch: false }];

			const parts = [];
			const lowerText = text.toLowerCase();
			const lowerBuffer = this.typeBuffer.toLowerCase();
			let lastIndex = 0;

			while (true) {
				const matchIndex = lowerText.indexOf(lowerBuffer, lastIndex);
				if (matchIndex === -1) {
					if (lastIndex < text.length) {
						parts.push({
							text: text.slice(lastIndex),
							isMatch: false
						});
					}
					break;
				}

				if (matchIndex > lastIndex) {
					parts.push({
						text: text.slice(lastIndex, matchIndex),
						isMatch: false
					});
				}

				parts.push({
					text: text.slice(matchIndex, matchIndex + lowerBuffer.length),
					isMatch: true
				});

				lastIndex = matchIndex + lowerBuffer.length;
			}

			return parts;
		},
	},
	components: {
		MenuOrBottomSheet,
	},
});
